import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { Button } from '../../../components/ui/Button';
import { Spinner } from '../../../components/ui/Spinner';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

export const CardDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCard = async () => {
      try {
        const { data, error } = await supabase
          .from('cards')
          .select('*')
          .eq('id', id)
          .eq('user_id', user.id)
          .single();
          
        if (error) throw error;
        setCard(data);
      } catch (err) {
        setError('Card not found or you do not have permission to view it.');
      } finally {
        setLoading(false);
      }
    };
    
    if (user?.id && id) fetchCard();
  }, [user, id]);

  if (loading) {
    return <div className="flex justify-center py-20"><Spinner /></div>;
  }

  if (error || !card) {
    return (
      <div className="text-center py-20">
        <h2 className="text-2xl font-bold text-slate-900 mb-4">{error}</h2>
        <Button onClick={() => navigate('/app/cards')}>Back to Cards</Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl pb-12">
      <div className="mb-6 flex items-center text-sm font-medium text-slate-500">
        <button onClick={() => navigate('/app/cards')} className="hover:text-slate-900 flex items-center">
          <SafeIcon icon={FiIcons.FiArrowLeft} className="mr-1" /> Cards
        </button>
        <span className="mx-2">/</span>
        <span className="text-slate-900">{card.internal_name}</span>
      </div>

      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold text-slate-900">{card.internal_name}</h1>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${card.status === 'published' ? 'bg-green-100 text-green-800' : card.status === 'archived' ? 'bg-slate-100 text-slate-800' : 'bg-blue-100 text-blue-800'}`}>
              {card.status}
            </span>
          </div>
          <p className="text-slate-500 text-sm font-mono bg-slate-100 px-2 py-1 rounded inline-block">
            meetoi.com/c/{card.public_slug}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate(`/app/cards/${card.id}/analytics`)}>
            <SafeIcon icon={FiIcons.FiBarChart2} className="mr-2 h-4 w-4" /> Analytics
          </Button>
          <Button variant="secondary" onClick={() => navigate(`/app/cards/${card.id}/edit`)}>
            <SafeIcon icon={FiIcons.FiEdit2} className="mr-2 h-4 w-4" /> Edit Card
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Left Col: Metadata */}
        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4 border-b border-slate-100 pb-2">Card Details</h3>
            <dl className="space-y-4 text-sm">
              <div>
                <dt className="text-slate-500">Template</dt>
                <dd className="font-medium text-slate-900 capitalize">{card.template_id}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Created</dt>
                <dd className="font-medium text-slate-900">{new Date(card.created_at).toLocaleDateString()}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Last Updated</dt>
                <dd className="font-medium text-slate-900">{new Date(card.updated_at).toLocaleDateString()}</dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Right Col: Content Viewer */}
        <div className="md:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-6 border-b border-slate-100 pb-2">Public Identity</h3>
            
            <div className="flex items-start gap-6 mb-8">
              <div className="w-20 h-20 rounded-full overflow-hidden bg-slate-100 border border-slate-200 shrink-0">
                {card.profile_image_url ? (
                  <img src={card.profile_image_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-400">
                    <SafeIcon icon={FiIcons.FiUser} className="text-3xl" />
                  </div>
                )}
              </div>
              <div>
                <h4 className="text-2xl font-bold text-slate-900">{card.full_name}</h4>
                <p className="text-lg text-slate-600">{card.job_title} {card.company_name ? `at ${card.company_name}` : ''}</p>
                {card.location && <p className="text-sm text-slate-500 mt-1 flex items-center"><SafeIcon icon={FiIcons.FiMapPin} className="mr-1"/>{card.location}</p>}
              </div>
            </div>

            {card.bio && (
              <div className="mb-8">
                <h5 className="text-sm font-medium text-slate-500 mb-2">About</h5>
                <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{card.bio}</p>
              </div>
            )}

            <div>
              <h5 className="text-sm font-medium text-slate-500 mb-3">Contact Information</h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {card.email && (
                  <div className="flex items-center px-4 py-3 bg-slate-50 rounded-lg border border-slate-100">
                    <SafeIcon icon={FiIcons.FiMail} className="text-slate-400 mr-3" />
                    <span className="text-sm font-medium text-slate-900 truncate">{card.email}</span>
                  </div>
                )}
                {card.phone && (
                  <div className="flex items-center px-4 py-3 bg-slate-50 rounded-lg border border-slate-100">
                    <SafeIcon icon={FiIcons.FiPhone} className="text-slate-400 mr-3" />
                    <span className="text-sm font-medium text-slate-900">{card.phone}</span>
                  </div>
                )}
                {card.website && (
                  <div className="flex items-center px-4 py-3 bg-slate-50 rounded-lg border border-slate-100 sm:col-span-2">
                    <SafeIcon icon={FiIcons.FiGlobe} className="text-slate-400 mr-3" />
                    <a href={card.website} target="_blank" rel="noreferrer" className="text-sm font-medium text-blue-600 hover:underline truncate">
                      {card.website}
                    </a>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};